/**
 * Hide and Seek Teams statistics: see ../types/hnstStatistics.ts. Worked out
 * from the game alone, over the rounds the standings count, so players' and
 * admins' clients (live and archived games) show the same numbers.
 */
import { isHnstSettings } from '../types/round';
import { getPlayerDisplayName, isHnsPlayerDisqualified, isHnstRoundPlayer } from '../types/player';
import { HNST_TEAM_KEYS, otherHnstTeam } from '../types/hnst';
import { HNST_SURVIVAL_RECORD_MIN_ROUNDS, } from '../types/hnstStatistics';
import { calculateHnsPlayerRoundScore, calculateHnstRoundTeamScore } from './scoring';
export function getHnstHiderOutcome(player) {
    if (isHnsPlayerDisqualified(player))
        return 'disqualified';
    if (player.capturedByPlayerId)
        return 'caught';
    if (player.eliminatedByDeaths && player.capturedAt != null)
        return 'eliminated';
    if (player.capturedAt != null)
        return 'droppedOut';
    return 'survived';
}
/** When seeking began (server epoch ms), or null for a round never started. */
function seekingStartedAt(round) {
    if (!isHnstSettings(round.settings) || round.startedAt == null)
        return null;
    return round.startedAt + round.settings.hidingDuration * 1000;
}
/** Whole seconds from seeking start to `at`, or null. */
function secondsIntoSeeking(round, at) {
    const start = seekingStartedAt(round);
    if (start === null || at == null)
        return null;
    return Math.max(0, Math.floor((at - start) / 1000));
}
/** A hider's time hidden, before penalties; null when disqualified. */
function hiddenSeconds(player, round) {
    if (isHnsPlayerDisqualified(player))
        return null;
    return secondsIntoSeeking(round, player.capturedAt ?? round.endedAt);
}
const sum = (values) => values.reduce((total, v) => total + v, 0);
const mean = (values) => (values.length > 0 ? sum(values) / values.length : null);
const ratio = (part, whole) => (whole > 0 ? part / whole : null);
const best = (values, pick) => values.length > 0 ? (pick === 'max' ? Math.max(...values) : Math.min(...values)) : null;
/** Everyone level on the highest count above 0. */
function leaders(counts) {
    const value = best([...counts.values()], 'max');
    if (value === null || value <= 0)
        return null;
    return { value, playerIds: [...counts].filter(([, n]) => n === value).map(([id]) => id) };
}
/** The best value of the entries and everyone who holds it. */
function record(entries, pick) {
    const value = best(entries.map((e) => e.value), pick);
    if (value === null)
        return null;
    const holders = [];
    const seen = new Set();
    for (const { playerId, roundIndex, value: v } of entries) {
        const key = `${playerId}:${roundIndex ?? ''}`;
        if (v !== value || seen.has(key))
            continue;
        seen.add(key);
        holders.push(roundIndex === undefined ? { playerId } : { playerId, roundIndex });
    }
    return { value, holders };
}
function countRuleUses(player, into) {
    let deaths = 0;
    for (const use of player.ruleUses ?? []) {
        if (use.rule === 'deaths')
            deaths++;
        else
            into[use.rule] = (into[use.rule] ?? 0) + 1;
    }
    return deaths;
}
function newTeam(team) {
    return {
        stats: {
            team,
            totalSeconds: 0,
            meanSeconds: 0,
            pointsSeconds: 0,
            roundsHidden: 0,
            roundsSought: 0,
            pairsWon: 0,
            pairsLost: 0,
            pairsDrawn: 0,
            hiding: {
                hidersFielded: 0,
                survived: 0,
                caught: 0,
                eliminated: 0,
                droppedOut: 0,
                disqualified: 0,
                survivalRate: null,
                averageRoundScore: null,
                bestRoundScore: null,
                averageSecondsBeforeCaught: null,
                bestTeamAverage: null,
                worstTeamAverage: null,
                penaltySeconds: 0,
            },
            seeking: {
                finds: 0,
                findsPerRound: null,
                hidersFaced: 0,
                findRate: null,
                averageFindSeconds: null,
                fastestFindSeconds: null,
                averageFirstFindSeconds: null,
                cleanSweeps: 0,
            },
        },
        roundScores: [],
        caughtSeconds: [],
        meanAverages: [],
        findSeconds: [],
        firstFindSeconds: [],
    };
}
function newPlayer(game, roundPlayer) {
    const { playerId } = roundPlayer;
    const known = game.players.some((p) => p.playerId === playerId);
    return {
        stats: {
            playerId,
            characterName: known || !roundPlayer.characterName ? getPlayerDisplayName(playerId, game.players) : roundPlayer.characterName,
            team: roundPlayer.team,
            roundsPlayed: 0,
            deaths: 0,
            hiding: {
                rounds: 0,
                survived: 0,
                caught: 0,
                eliminated: 0,
                droppedOut: 0,
                disqualified: 0,
                survivalRate: null,
                totalHiddenSeconds: 0,
                averageHiddenSeconds: null,
                longestHideSeconds: null,
                quickestCaughtSeconds: null,
                scoreSeconds: 0,
                penaltySeconds: 0,
                pointsEarned: 0,
                ruleUses: {},
            },
            seeking: {
                rounds: 0,
                catches: 0,
                catchesPerRound: null,
                bestRoundCatches: 0,
                fastestCatchSeconds: null,
                averageCatchSeconds: null,
                firstFinds: 0,
                nemesis: null,
                pointsEarned: 0,
                ruleUses: {},
            },
        },
        hiddenSeconds: [],
        caughtSeconds: [],
        catchSeconds: [],
        catchesByOpponent: new Map(),
    };
}
/** Pair consecutive rounds in which each team hid once; see HnstRoundPair. */
function pairRounds(rounds) {
    const pairs = [];
    const close = (members) => {
        const teamSeconds = { rotworms: 0, frogdogs: 0 };
        for (const round of members)
            for (const team of HNST_TEAM_KEYS)
                teamSeconds[team] += round.teamSeconds[team];
        const complete = members.length === 2;
        const winner = !complete || teamSeconds.rotworms === teamSeconds.frogdogs
            ? null
            : teamSeconds.rotworms > teamSeconds.frogdogs ? 'rotworms' : 'frogdogs';
        pairs.push({ roundIndexes: members.map((r) => r.roundIndex), teamSeconds, complete, winner });
    };
    let pending = null;
    for (const round of rounds) {
        if (pending && pending.hidingTeam !== round.hidingTeam) {
            close([pending, round]);
            pending = null;
        }
        else {
            if (pending)
                close([pending]);
            pending = round;
        }
    }
    if (pending)
        close([pending]);
    return pairs;
}
/**
 * A Hide and Seek Teams game's statistics over its completed, enabled rounds.
 */
export function calculateHnstGameStatistics(game) {
    const teams = new Map(HNST_TEAM_KEYS.map((team) => [team, newTeam(team)]));
    const players = new Map();
    const rounds = [];
    const hideEntries = [];
    const caughtEntries = [];
    const catchEntries = [];
    const roundCatchEntries = [];
    game.rounds.forEach((round, roundIndex) => {
        if (round.status !== 'completed' || round.disabled)
            return;
        const score = calculateHnstRoundTeamScore(round, roundIndex);
        if (!score)
            return;
        const { hidingTeam, scoring, teamSeconds } = score;
        const seekingTeam = otherHnstTeam(hidingTeam);
        const hiding = teams.get(hidingTeam);
        const seeking = teams.get(seekingTeam);
        const roundPlayers = round.players.filter(isHnstRoundPlayer);
        const hiders = roundPlayers.filter((p) => p.team === hidingTeam);
        const secondsPerFind = scoring.mode === 'points' ? scoring.secondsPerFind : 0;
        for (const [team, { stats }] of teams) {
            stats.totalSeconds += teamSeconds[team];
            if (scoring.mode === 'mean')
                stats.meanSeconds += teamSeconds[team];
            else
                stats.pointsSeconds += teamSeconds[team];
        }
        hiding.stats.roundsHidden++;
        seeking.stats.roundsSought++;
        hiding.roundScores.push(teamSeconds[hidingTeam]);
        if (scoring.mode === 'mean')
            hiding.meanAverages.push(score.averageSeconds);
        // Each player's round.
        const catchesBySeeker = new Map();
        const finds = [];
        const outcomes = { disqualified: 0, caught: 0, eliminated: 0, droppedOut: 0, survived: 0 };
        let penaltySeconds = 0;
        const hidden = new Map();
        for (const roundPlayer of roundPlayers) {
            let tally = players.get(roundPlayer.playerId);
            if (!tally) {
                tally = newPlayer(game, roundPlayer);
                players.set(roundPlayer.playerId, tally);
            }
            const { stats } = tally;
            stats.team = roundPlayer.team;
            stats.roundsPlayed++;
            const isHider = roundPlayer.team === hidingTeam;
            stats.deaths += countRuleUses(roundPlayer, isHider ? stats.hiding.ruleUses : stats.seeking.ruleUses);
            if (!isHider) {
                stats.seeking.rounds++;
                continue;
            }
            const outcome = getHnstHiderOutcome(roundPlayer);
            outcomes[outcome]++;
            stats.hiding.rounds++;
            stats.hiding[outcome]++;
            stats.hiding.scoreSeconds += roundPlayer.finalScoreSeconds ?? calculateHnsPlayerRoundScore(roundPlayer, round);
            stats.hiding.penaltySeconds += roundPlayer.totalPenaltySeconds ?? 0;
            penaltySeconds += roundPlayer.totalPenaltySeconds ?? 0;
            if (outcome === 'survived' && scoring.mode === 'points' && scoring.survivorsScore) {
                stats.hiding.pointsEarned += secondsPerFind;
            }
            const seconds = hiddenSeconds(roundPlayer, round);
            if (seconds !== null) {
                tally.hiddenSeconds.push(seconds);
                hidden.set(roundPlayer.playerId, seconds);
                hideEntries.push({ playerId: roundPlayer.playerId, roundIndex, value: seconds });
            }
            if (outcome === 'caught') {
                const seekerId = roundPlayer.capturedByPlayerId;
                catchesBySeeker.set(seekerId, (catchesBySeeker.get(seekerId) ?? 0) + 1);
                finds.push({ hiderId: roundPlayer.playerId, seekerId, seconds });
                if (seconds !== null) {
                    tally.caughtSeconds.push(seconds);
                    hiding.caughtSeconds.push(seconds);
                    seeking.findSeconds.push(seconds);
                    caughtEntries.push({ playerId: roundPlayer.playerId, roundIndex, value: seconds });
                    catchEntries.push({ playerId: seekerId, roundIndex, value: seconds });
                }
            }
        }
        // The seekers' catches.
        for (const [seekerId, catches] of catchesBySeeker) {
            const seeker = players.get(seekerId);
            roundCatchEntries.push({ playerId: seekerId, roundIndex, value: catches });
            if (!seeker)
                continue;
            seeker.stats.seeking.catches += catches;
            seeker.stats.seeking.bestRoundCatches = Math.max(seeker.stats.seeking.bestRoundCatches, catches);
            seeker.stats.seeking.pointsEarned += catches * secondsPerFind;
        }
        for (const find of finds) {
            const seeker = players.get(find.seekerId);
            if (!seeker)
                continue;
            if (find.seconds !== null)
                seeker.catchSeconds.push(find.seconds);
            seeker.catchesByOpponent.set(find.hiderId, (seeker.catchesByOpponent.get(find.hiderId) ?? 0) + 1);
        }
        const findTimes = finds.map((f) => f.seconds).filter((s) => s !== null);
        const firstFindSeconds = best(findTimes, 'min');
        if (firstFindSeconds !== null) {
            seeking.firstFindSeconds.push(firstFindSeconds);
            const firstFinders = new Set(finds.filter((f) => f.seconds === firstFindSeconds).map((f) => f.seekerId));
            for (const seekerId of firstFinders) {
                const seeker = players.get(seekerId);
                if (seeker)
                    seeker.stats.seeking.firstFinds++;
            }
        }
        hiding.stats.hiding.hidersFielded += hiders.length;
        hiding.stats.hiding.survived += outcomes.survived;
        hiding.stats.hiding.caught += outcomes.caught;
        hiding.stats.hiding.eliminated += outcomes.eliminated;
        hiding.stats.hiding.droppedOut += outcomes.droppedOut;
        hiding.stats.hiding.disqualified += outcomes.disqualified;
        hiding.stats.hiding.penaltySeconds += penaltySeconds;
        seeking.stats.seeking.finds += score.found;
        seeking.stats.seeking.hidersFaced += score.teamSize;
        if (score.found > 0 && score.survived === 0)
            seeking.stats.seeking.cleanSweeps++;
        rounds.push({
            roundIndex,
            hidingTeam,
            scoring,
            teamSize: score.teamSize,
            found: score.found,
            survived: score.survived,
            eliminated: outcomes.eliminated,
            droppedOut: outcomes.droppedOut,
            disqualified: outcomes.disqualified,
            teamSeconds,
            averageSeconds: score.averageSeconds,
            seekingSeconds: secondsIntoSeeking(round, round.endedAt),
            firstFindSeconds,
            lastFindSeconds: best(findTimes, 'max'),
            topSeekers: leaders(catchesBySeeker),
            longestHiders: leaders(hidden),
            penaltySeconds,
        });
    });
    // Rates and averages, once everything is counted.
    const pairs = pairRounds(rounds);
    for (const { stats, roundScores, caughtSeconds, meanAverages, findSeconds, firstFindSeconds } of teams.values()) {
        const { hiding, seeking } = stats;
        hiding.survivalRate = ratio(hiding.survived, hiding.hidersFielded);
        hiding.averageRoundScore = mean(roundScores);
        hiding.bestRoundScore = best(roundScores, 'max');
        hiding.averageSecondsBeforeCaught = mean(caughtSeconds);
        hiding.bestTeamAverage = best(meanAverages, 'max');
        hiding.worstTeamAverage = best(meanAverages, 'min');
        seeking.findsPerRound = ratio(seeking.finds, stats.roundsSought);
        seeking.findRate = ratio(seeking.finds, seeking.hidersFaced);
        seeking.averageFindSeconds = mean(findSeconds);
        seeking.fastestFindSeconds = best(findSeconds, 'min');
        seeking.averageFirstFindSeconds = mean(firstFindSeconds);
        for (const pair of pairs) {
            if (!pair.complete)
                continue;
            if (pair.winner === null)
                stats.pairsDrawn++;
            else if (pair.winner === stats.team)
                stats.pairsWon++;
            else
                stats.pairsLost++;
        }
    }
    for (const tally of players.values()) {
        const { hiding, seeking } = tally.stats;
        hiding.survivalRate = ratio(hiding.survived, hiding.rounds);
        hiding.totalHiddenSeconds = sum(tally.hiddenSeconds);
        hiding.averageHiddenSeconds = mean(tally.hiddenSeconds);
        hiding.longestHideSeconds = best(tally.hiddenSeconds, 'max');
        hiding.quickestCaughtSeconds = best(tally.caughtSeconds, 'min');
        seeking.catchesPerRound = ratio(seeking.catches, seeking.rounds);
        seeking.fastestCatchSeconds = best(tally.catchSeconds, 'min');
        seeking.averageCatchSeconds = mean(tally.catchSeconds);
        seeking.nemesis = leaders(tally.catchesByOpponent);
    }
    const playerList = [...players.values()]
        .map((t) => t.stats)
        .sort((a, b) => a.characterName.localeCompare(b.characterName));
    // Players' records go to values above 0: nobody holds "most catches" with none.
    const perPlayer = (value) => playerList
        .map((p) => ({ playerId: p.playerId, value: value(p) }))
        .filter((e) => e.value !== null && e.value > 0);
    const records = {
        mostCatches: record(perPlayer((p) => p.seeking.catches), 'max'),
        mostCatchesInRound: record(roundCatchEntries, 'max'),
        longestHide: record(hideEntries, 'max'),
        quickestCaught: record(caughtEntries, 'min'),
        fastestCatch: record(catchEntries, 'min'),
        bestSurvivalRate: record(perPlayer((p) => (p.hiding.rounds >= HNST_SURVIVAL_RECORD_MIN_ROUNDS ? p.hiding.survivalRate : null)), 'max'),
        mostPenalised: record(perPlayer((p) => p.hiding.penaltySeconds), 'max'),
        mostDeaths: record(perPlayer((p) => p.deaths), 'max'),
    };
    return {
        totalRounds: rounds.length,
        teams: Object.fromEntries([...teams].map(([team, tally]) => [team, tally.stats])),
        pairs,
        players: playerList,
        rounds,
        records,
    };
}
